import { differenceInMilliseconds, formatDuration, intervalToDuration, FormatDistanceToken } from 'date-fns';
import { enUS } from 'date-fns/locale/en-US';
import { formatInTimeZone, fromZonedTime, getTimezoneOffset, format, toZonedTime } from 'date-fns-tz';
import { CalendarDateTime, DateValue, getLocalTimeZone, parseDateTime, ZonedDateTime } from '@internationalized/date';
import { RangeValue } from '@ds';
import { isString } from 'lodash-es';
import { SelectItemType } from '@/types';
import {
  FALLBACK_HHMM,
  HOUR_IN_MS,
  IANA_TIMEZONE_LIST,
  ISO8601_MIN_YEAR,
  TIMESTAMP_REGEX,
} from '@/constants/date.constants';

export const TIME_WITH_TIMEZONE_FORMAT = 'HH:mm zzz';

type FormatDateOptions = {
  placeholder?: string | null;
  timezone?: string | null;
};

const getAbbreviationMapping = (date: Date): Record<string, string> => {
  const mapping: Record<string, string> = {};

  IANA_TIMEZONE_LIST.forEach((tz) => {
    const zoned = toZonedTime(date, tz);
    const abbr = format(zoned, 'zzz', { timeZone: tz });
    mapping[abbr] = tz;
  });

  return mapping;
};

// Abbreviations change with daylight saving time (CST in winter, CDT in summer), so January and July
// add the other season's names; today's abbreviations keep the zone they map to now.
export const getTimezoneMapping = (): Record<string, string> => {
  const now = new Date();
  const year = now.getUTCFullYear();

  return {
    ...getAbbreviationMapping(new Date(Date.UTC(year, 0, 1))),
    ...getAbbreviationMapping(new Date(Date.UTC(year, 6, 1))),
    ...getAbbreviationMapping(now),
  };
};

export const TIMEZONE_MAP = getTimezoneMapping();

export const convertToIanaTimezone = (timezone?: string | null) => {
  const tz = timezone ?? getLocalTimeZone();

  return TIMEZONE_MAP[tz] ?? tz;
};

export const formatDate = (
  date: Date | number | string | undefined | null,
  formatStr: string,
  options: FormatDateOptions = {},
) => {
  if (!date) return options.placeholder ?? FALLBACK_HHMM;
  const myTimeZone = getLocalTimeZone();
  const timezone = convertToIanaTimezone(options.timezone) ?? myTimeZone;
  return formatInTimeZone(date, timezone, formatStr, { locale: enUS });
};

export const getUserTimezone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

export const getShortTzByIanaName = (ianaTimezone: string, date: Date = new Date()): string => {
  if (!ianaTimezone) return '';
  return formatDate(date, 'zzz', { timezone: ianaTimezone });
};

/**
 * @param timezone IANA timezone string
 * @param date Date for determening is it daylight or standart time. Uses today if param is not passed
 * @returns string
 */
export const getTimezoneWithCity = (timezone: string, date?: Date) => {
  if (!timezone) return '';

  const abbreviation = getShortTzByIanaName(timezone, date);

  const parts = timezone.includes('/')
    ? timezone
        .split('/')
        .reverse()
        .map((part) => part.replace(/_/g, ' '))
        .join(', ')
    : 'Unknown';

  return `${parts} (${abbreviation})`;
};

export const getTimezones = (date?: Date): SelectItemType[] => {
  return IANA_TIMEZONE_LIST.map((t) => ({
    value: t,
    name: getTimezoneWithCity(t, date),
  }));
};

export const convertMsToTime = (ms: number): { sign: string; hours: number; minutes: number } => {
  const totalMinutes = ms / (1000 * 60); // Convert milliseconds to minutes
  const sign = totalMinutes >= 0 ? '+' : '-'; // Determine if the offset is positive or negative

  const absMinutes = Math.abs(totalMinutes);
  const hours = Math.floor(absMinutes / 60); // Get the whole hours
  const minutes = absMinutes % 60; // Get the remaining minutes

  return { sign, hours, minutes: Math.floor(minutes) };
};

export const convertMillisecondsToReadableFormat = (milliseconds: number): string => {
  const duration = intervalToDuration({ start: 0, end: milliseconds });
  const formatDistanceLocale = {
    xSeconds: '{{count}}s',
    xMinutes: '{{count}}m',
    xHours: '{{count}}h',
    xDays: '{{count}}d',
  };
  const shortEnLocale = {
    formatDistance: (token: FormatDistanceToken, count: number): string => {
      return (formatDistanceLocale as Record<string, string>)[token].replace('{{count}}', count.toString());
    },
  };

  return formatDuration(duration, { format: ['hours', 'minutes', 'seconds'], locale: shortEnLocale });
};

export const convertToTimezoneOffset = (milliseconds: number, showSign: boolean = true): string => {
  const { sign, hours, minutes } = convertMsToTime(milliseconds);

  // Format the hours and minutes as two-digit strings
  const formattedHours = hours.toString().padStart(2, '0');
  const formattedMinutes = minutes.toString().padStart(2, '0');

  if (!showSign) {
    return `${formattedHours}:${formattedMinutes}`;
  }

  return `${sign}${formattedHours}:${formattedMinutes}`;
};

export const convertMsToTimeString = (milliseconds: number, formatString: string = 'long'): string => {
  const { hours, minutes } = convertMsToTime(milliseconds);

  const formattedHours = hours.toString();
  const formattedMinutes = minutes.toString().padStart(2, '0');

  return formatString === 'long' ? `${formattedHours}h ${formattedMinutes}m` : `${formattedHours}:${formattedMinutes}h`;
};

export const checkTimestamp = (timestamp: string) => {
  return !!timestamp.match(TIMESTAMP_REGEX);
};

export const getFormattedTimeRange = (startTime: string, endTime: string, timezone: string = 'UTC') => {
  // sometimes aircraft availability response has timestamps without Z
  const fixedUtcStartTime = checkTimestamp(startTime) ? startTime : `${startTime}Z`;
  const fixedUtcEndTime = checkTimestamp(endTime) ? endTime : `${endTime}Z`;
  // TODO: add correct timezones for local time (used UTC for now)
  const formattedStartTime = formatDate(new Date(fixedUtcStartTime), 'dd MMM, HH:mm zzz', { timezone });
  const formattedEndTime = formatDate(new Date(fixedUtcEndTime), 'dd MMM, HH:mm zzz', { timezone });
  const timeDiff = differenceInMilliseconds(new Date(fixedUtcEndTime), new Date(fixedUtcStartTime));
  const timeDiffInHours = convertMsToTimeString(timeDiff);

  return `${formattedStartTime} - ${formattedEndTime} (${timeDiffInHours})`;
};

export const getTimeDifference = (start: Date, end: Date = new Date()) => {
  const differenceMs = Math.abs(Number(end) - Number(start));
  const differenceDate = new Date(differenceMs);
  const years = differenceDate.getUTCFullYear() - 1970;
  const months = differenceDate.getUTCMonth();
  const days = differenceDate.getUTCDate() - 1;
  const hours = differenceDate.getUTCHours();
  const minutes = differenceDate.getUTCMinutes();
  const seconds = differenceDate.getUTCSeconds();
  const milliseconds = differenceDate.getUTCMilliseconds();
  const SECONDS = Math.floor(differenceMs / 1000);
  const MINUTES = Math.floor(SECONDS / 60);
  const HOURS = Math.floor(MINUTES / 60);
  const DAYS = Math.floor(HOURS / 24);

  return {
    years,
    months,
    days,
    hours,
    minutes,
    seconds,
    milliseconds,
    HOURS,
    MINUTES,
    SECONDS,
    MILLISECONDS: differenceMs,
    DAYS,
  };
};

type FormatTimeDifferenceOptions = {
  hours?: boolean;
  minutes?: boolean;
  seconds?: boolean;
  milliseconds?: boolean;
};

export const formatTimeDifference = (
  start: Date,
  end: Date = new Date(),
  { hours, minutes, seconds, milliseconds }: FormatTimeDifferenceOptions = { hours: true, minutes: true },
) => {
  const diff = getTimeDifference(start, end);
  const timeItems = [];

  if (hours) timeItems.push(diff.hours.toString().padStart(2, '0'));
  if (minutes) timeItems.push(diff.minutes.toString().padStart(2, '0'));
  if (seconds) timeItems.push(diff.seconds.toString().padStart(2, '0'));
  if (milliseconds) timeItems.push(diff.milliseconds.toString().padStart(3, '0'));

  return timeItems.join(':');
};

export const formatDateTimeString = (date?: string | null, time?: string | null, timezone?: string | null) => {
  if (!date || !time) return 'mm/dd/yyyy --:--';
  const timeZone = convertToIanaTimezone(timezone);
  const dateObj = fromZonedTime(new Date(`${date} ${time}`), timeZone);
  return formatDate(dateObj, 'MM/dd/yyyy HH:mm zzz', { timezone: timeZone });
};

/**
 * Get formatted datetime string
 * @param timestamp Timestamp string in ISO format
 * @param timeZone IANA timezone string
 * @param formatString Formatting string
 * @returns Formatted datetime string
 */
export const formatTimestamp = (
  timestamp?: string,
  timeZone?: string,
  formatString: string = 'MM/dd/yyyy HH:mm zzz',
): string => {
  if (!timestamp || !timeZone) return '';
  return formatDate(timestamp, formatString, { timezone: timeZone });
};

export const getTimeZonedDate = (date?: string | null, time?: string | null, timezone?: string | null) => {
  if (!date || !time || !timezone) return null;

  const dateObj = new Date(`${date} ${time}`);
  // getTimezoneOffset gets date from dateObj and calculates offset for provided timezone.
  // Due to USA timezones are before UTC it calculates daylight offsets incorrectly on daylight time change.
  // Can fail 2 times a year.
  const dateObjForTimezoneOffset = fromZonedTime(new Date(`${date} ${time}`), 'UTC');

  return new ZonedDateTime(
    dateObj.getFullYear(),
    dateObj.getMonth() + 1,
    dateObj.getDate(),
    timezone,
    getTimezoneOffset(timezone, dateObjForTimezoneOffset),
    dateObj.getHours(),
    dateObj.getMinutes(),
  );
};

export const getTimeZonedDateString = (date?: string | null, time?: string | null, timezone?: string | null) => {
  if (!date || !time || !timezone) return null;

  const zonedDate = getTimeZonedDate(date, time, timezone);

  return zonedDate?.toString() || null;
};

export const formatDateTimeStringToTimeZone = (
  dateString?: string | null,
  timezone?: string | null,
  formatStr: string = 'MM/dd/yyyy HH:mm',
) => {
  if (!dateString) return FALLBACK_HHMM;
  const ianaTimeZone = convertToIanaTimezone(timezone);
  const date = new Date(dateString);
  const dateTimeString = formatDate(date, formatStr, { timezone: ianaTimeZone });
  const shortTzName = getShortTzByIanaName(ianaTimeZone, date);

  return `${dateTimeString} ${shortTzName}`;
};

export const validateDateTimeValue = (dateTime: DateValue | null): DateValue | null => {
  if (!dateTime) {
    return null;
  }

  const isCorrectIsoYear = ISO8601_MIN_YEAR <= dateTime.year;

  if (!isCorrectIsoYear) {
    return null;
  }

  return dateTime;
};

export const getTzDateTimeString = (dateTimeString: string): string => {
  if (dateTimeString.includes('[')) return dateTimeString;
  const dateTime = dateTimeString.endsWith('Z') ? dateTimeString.slice(0, -1) : dateTimeString;
  return `${dateTime}[UTC]`;
};

export const removeTimeZoneName = (isoString: string) => {
  if (!isoString.includes('[')) return isoString;
  return isoString.replace(/\[.*?]$/, '');
};

// TODO: This utility is temporary and will be removed once the backend is updated to send timestamps in the proper format directly.
export const convertToUserLocalTime = (
  date?: string | null,
  time?: string | null,
  sourceTimezone?: string | null,
  addMinutes?: number,
): string => {
  if (!date || !time || !sourceTimezone) return 'mm/dd/yyyy --:--';

  const localDate = fromZonedTime(new Date(`${date} ${time}`), sourceTimezone);
  if (Number.isNaN(localDate.getTime())) return 'mm/dd/yyyy --:--';

  // buffer minutes to give TransMedics flexible time to react
  if (addMinutes) {
    localDate.setMinutes(localDate.getMinutes() + addMinutes);
  }

  return formatInTimeZone(localDate, getUserTimezone(), 'MM/dd/yyyy HH:mm zzz', { locale: enUS });
};

export const createDateRangeFromArrays = (startString?: string, endString?: string): RangeValue<DateValue> | null => {
  if (!isString(startString) && !isString(endString)) return null;

  const startDateValue = startString?.length ? parseDateTime(startString) : null;
  const endDateValue = endString?.length ? parseDateTime(endString) : null;

  if (!startDateValue && !endDateValue) return null;

  if (startDateValue && endDateValue) {
    return { start: startDateValue, end: endDateValue };
  }

  if (startDateValue && !endDateValue) {
    return { start: startDateValue, end: startDateValue };
  }

  if (!startDateValue && endDateValue) {
    return { start: endDateValue, end: endDateValue };
  }

  return null;
};

export const formatCalendarDateTime = (dateValue: DateValue | null, isStartOfDay: boolean) => {
  if (!dateValue) {
    return null;
  }

  const hours = isStartOfDay ? 0 : 23;
  const minutes = isStartOfDay ? 0 : 59;
  const seconds = isStartOfDay ? 0 : 59;

  const dateTime = new CalendarDateTime(
    dateValue.calendar,
    dateValue.era,
    dateValue.year,
    dateValue.month,
    dateValue.day,
    hours,
    minutes,
    seconds,
  );

  return dateTime.toString();
};

export const truncateToMinutes = (zdt: ZonedDateTime): ZonedDateTime => {
  return zdt.set({ second: 0, millisecond: 0 });
};

export const zonedDateToString = (zonedDate: ZonedDateTime, customTimeZone?: string): string => {
  const month = zonedDate.month.toString().padStart(2, '0');
  const day = zonedDate.day.toString().padStart(2, '0');
  const year = zonedDate.year.toString();
  const hours = zonedDate.hour.toString().padStart(2, '0');
  const minutes = zonedDate.minute.toString().padStart(2, '0');
  const timezone = getShortTzByIanaName(customTimeZone || zonedDate.timeZone, zonedDate.toDate());

  return `${month}/${day}/${year} ${hours}:${minutes} ${timezone}`;
};

export const dateTimeToISO = (date: string, time: string, timeZone: string) => {
  return fromZonedTime(new Date(`${date} ${time}`), timeZone).toISOString();
};

export const msToFixedHours = (ms: number): string => `${(ms / HOUR_IN_MS).toFixed(2)}h`;

export type ParsedDonorORTime = {
  donorHospitalDate: string;
  donorHospitalTime: string;
};

/** Splits opobff GET `donorORTime` into form-compatible date/time strings in the case timezone. */
export const parseDonorORTime = (
  donorORTime: string | null | undefined,
  timeZone: string | null | undefined,
): ParsedDonorORTime | null => {
  if (!donorORTime) return null;

  const ianaTimezone = convertToIanaTimezone(timeZone ?? getUserTimezone());

  return {
    donorHospitalDate: formatInTimeZone(donorORTime, ianaTimezone, 'yyyy-MM-dd'),
    donorHospitalTime: formatInTimeZone(donorORTime, ianaTimezone, 'HH:mm:ss'),
  };
};

export const getUTCStringWithoutMs = (dateString: string): string => {
  const stringWithoutMs = dateString.match(/[^.]*/)?.[0];

  return `${stringWithoutMs}Z`;
};

export const getZonedUTCStringWithoutMs = (zonedDate: ZonedDateTime): string => {
  const utcString = zonedDate.toAbsoluteString();

  return getUTCStringWithoutMs(utcString);
};
